import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import ResolveWordmark from "../components/ResolveWordmark";

function passwordStrength(pw) {
  let score = 0;
  if (pw.length >= 10) score++;
  if (/[A-Za-z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (pw.length >= 14) score++;
  return Math.min(score, 4);
}

function StrengthMeter({ pw }) {
  const score = passwordStrength(pw);
  const labels = ["", "Weak", "Fair", "Good", "Strong"];
  const colors = ["#E2E8F0", "#DC2626", "#C2410C", "#5EAB45", "#047857"];
  return (
    <div className="mt-1.5">
      <div className="flex h-1.5 gap-1">
        {[1, 2, 3, 4].map((i) => (
          <span
            key={i}
            className="flex-1 rounded-full"
            style={{
              backgroundColor: i <= score ? colors[score] : "#E2E8F0",
            }}
          />
        ))}
      </div>
      <p
        className="mt-1 text-[11px] font-medium"
        style={{ color: score >= 1 ? colors[score] : "#94A3B8" }}
      >
        {pw ? labels[score] || "Very weak" : "Use ≥10 chars, with letter, number & symbol"}
      </p>
    </div>
  );
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [validity, setValidity] = useState({ checking: true, valid: false, message: "" });
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setValidity({ checking: false, valid: false, message: "Reset link is invalid or missing." });
      return;
    }
    let cancelled = false;
    api
      .get(`/auth/reset-password/validate?token=${encodeURIComponent(token)}`)
      .then(() => {
        if (!cancelled) setValidity({ checking: false, valid: true, message: "" });
      })
      .catch((e) => {
        if (cancelled) return;
        const status = e?.response?.status;
        const message =
          status === 410
            ? "This reset link has expired or has already been used. Request a new one."
            : "Reset link is invalid. Request a new one.";
        setValidity({ checking: false, valid: false, message });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated. Please sign in.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Reset failed";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative flex min-h-screen flex-col bg-brand-page" data-testid="reset-password-page">
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-72 navy-curve"
        aria-hidden="true"
      />
      <div className="relative z-10 mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center px-4 py-10">
        <div className="mb-6 flex flex-col items-center gap-2">
          <img
            src="/brand/goodwin-resolve-logo.png"
            alt="Goodwin Resolve"
            className="h-16 w-16 object-contain"
          />
          <ResolveWordmark size="lg" scheme="navy" />
        </div>

        <div className="brand-card w-full p-7 sm:p-8">
          {validity.checking && (
            <p className="text-sm text-[color:var(--page-fg-secondary)]" data-testid="reset-checking">
              Verifying your reset link…
            </p>
          )}

          {!validity.checking && !validity.valid && (
            <div className="text-center" data-testid="reset-invalid">
              <AlertCircle className="mx-auto h-10 w-10" style={{ color: "#DC2626" }} />
              <h1 className="mt-3 text-xl font-semibold text-[color:var(--page-fg)]">
                Reset link unusable
              </h1>
              <p className="mt-2 text-sm text-[color:var(--page-fg-secondary)]">
                {validity.message}
              </p>
              <Link
                to="/forgot-password"
                className="mt-4 inline-flex items-center text-sm font-medium text-brand-navy hover:text-brand-green hover:underline"
                data-testid="reset-request-new-link"
              >
                Request a new link
              </Link>
            </div>
          )}

          {!validity.checking && validity.valid && !done && (
            <>
              <h1 className="text-xl font-semibold tracking-tight text-[color:var(--page-fg)]">
                Set a new password
              </h1>
              <p className="mt-1 text-sm text-[color:var(--page-fg-secondary)]">
                Choose a strong password. After saving, you'll be signed out
                of any other devices.
              </p>
              <form onSubmit={submit} className="mt-5 space-y-4" data-testid="reset-password-form">
                <div className="space-y-1.5">
                  <Label htmlFor="pw" className="text-xs font-medium text-[color:var(--page-fg-secondary)]">
                    New password
                  </Label>
                  <Input
                    id="pw"
                    type="password"
                    required
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    className="h-11 rounded-xl border-[color:var(--border-subtle)] bg-white"
                    data-testid="reset-new-password"
                  />
                  <StrengthMeter pw={pw} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="confirm" className="text-xs font-medium text-[color:var(--page-fg-secondary)]">
                    Confirm new password
                  </Label>
                  <Input
                    id="confirm"
                    type="password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="h-11 rounded-xl border-[color:var(--border-subtle)] bg-white"
                    data-testid="reset-confirm-password"
                  />
                </div>
                {error && (
                  <p
                    className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                    data-testid="reset-error"
                  >
                    {error}
                  </p>
                )}
                <Button
                  type="submit"
                  disabled={submitting}
                  className="h-11 w-full rounded-xl font-semibold text-white"
                  style={{ backgroundColor: "#1E3A5F" }}
                  data-testid="reset-submit"
                >
                  {submitting ? "Saving…" : "Update password"}
                </Button>
              </form>
            </>
          )}

          {done && (
            <div className="text-center" data-testid="reset-success">
              <CheckCircle2 className="mx-auto h-12 w-12" style={{ color: "#5EAB45" }} />
              <h1 className="mt-3 text-xl font-semibold text-[color:var(--page-fg)]">
                Password updated
              </h1>
              <p className="mt-2 text-sm text-[color:var(--page-fg-secondary)]">
                You can now sign in with your new password.
              </p>
              <Button
                onClick={() => navigate("/login", { replace: true })}
                className="mt-5 h-11 rounded-xl px-6 font-semibold text-white"
                style={{ backgroundColor: "#1E3A5F" }}
                data-testid="reset-go-to-login"
              >
                <ArrowLeft className="mr-1 h-4 w-4" />
                Sign in
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
